import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import './CivitasPage.css'
import './civitas/fonts.css';
import './civitas/Civitas.css';

import ParallaxStars from './ParallaxStars';
import AppStoreBadgeSvg from './AppStoreBadgeSvg';
import {
  APP_STORE_URL,
  X_URL,
  PRIVACY_PATH,
  HERO,
  FEATURES,
  CIVILIZATIONS,
  SCREENSHOTS,
  FOOTER,
} from './civitas/content';

import siteImages from './images/site';
import { HEADER_LOGO_SIZES, CIVITAS_HERO_LOGO_SIZES } from './images/site-sizes.mjs';
import qrImage from './images/CivitasQrCode.png';

const civitasLogo = siteImages['civitas-icon'];
const civitasHeroLogo = siteImages['civitas-globe'];

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        {children}
      </div>
    </div>
  );
};

const CivitasPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add('dark-page');
    return () => document.documentElement.classList.remove('dark-page');
  }, []);

  const [isQRModalOpen, setIsQRModalOpen] = useState(false);

  const handleBurgerClick = (e) => {
    setIsMenuOpen(e.target.checked);
  };

  return (
    <div className="container civitas-container cv-page">
      <ParallaxStars />
      <div className="glass-header">
        <div className="header-content">
          <div className="logo-container">
            <img
              src={civitasLogo.src}
              srcSet={civitasLogo.srcSet}
              sizes={HEADER_LOGO_SIZES}
              width={civitasLogo.width}
              height={civitasLogo.height}
              alt="Civitas Logo"
              className="logo-image"
              fetchpriority="high"
              decoding="async"
              onClick={() => window.scrollTo(0, 0)}
            />
          </div>

          <div className="header-controls">
            {/* Desktop only (see .cv-header-badge): shows the QR code to scan. */}
            <a
              className="cv-header-badge"
              href={APP_STORE_URL}
              onClick={(e) => {
                e.preventDefault();
                setIsQRModalOpen(true);
              }}
            >
              <AppStoreBadgeSvg width={120} height={40} />
            </a>

            <label className="burger" htmlFor="burger">
              <input
                type="checkbox"
                id="burger"
                checked={isMenuOpen}
                onChange={handleBurgerClick}
              />
              <span></span>
              <span></span>
              <span></span>
            </label>
          </div>

          {isMenuOpen && (
            <div className="dropdown-menu">
              {/* <div className="menu-item" onClick={() => window.scrollTo(0, 0)}>Home</div> */}
              <div
                className="menu-item"
                onClick={() => document.getElementById('cv-features').scrollIntoView({
                  behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                })}
              >
                Features
              </div>
              <div
                className="menu-item"
                onClick={() => window.open(APP_STORE_URL, '_blank')}
              >
                Download
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={isQRModalOpen} onClose={() => setIsQRModalOpen(false)}>
        <div className="qr-container">
          <h2 className="qr-title">Scan to Download</h2>
          <img
            src={qrImage}
            alt="QR Code"
            className="qr-code"
          />
          <p className="qr-description">Scan this QR code with your mobile device to download the app</p>
        </div>
      </Modal>


      <main className="cv-main">
        <section className="cv-hero">
          <img
            src={civitasHeroLogo.src}
            srcSet={civitasHeroLogo.srcSet}
            sizes={CIVITAS_HERO_LOGO_SIZES}
            width={civitasHeroLogo.width}
            height={civitasHeroLogo.height}
            alt=""
            className="cv-hero-logo"
            fetchpriority="high"
          />
          <h1 className="cv-title">{HERO.title}</h1>
          <p className="cv-tagline">{HERO.tagline}</p>
          <p className="cv-summary">{HERO.summary}</p>
          <a className="cv-button" href={APP_STORE_URL}>{HERO.cta}</a>
          <p className="cv-hero-note">{HERO.note}</p>
        </section>

        {SCREENSHOTS.length > 0 && (
          <section className="cv-section" aria-label="Screenshots">
            <div className="cv-shots">
              {SCREENSHOTS.map((shot) => (
                <figure className="cv-card cv-shot" key={shot.src}>
                  <img
                    src={shot.src}
                    alt={shot.alt}
                    width={shot.width}
                    height={shot.height}
                    loading="lazy"
                    decoding="async"
                  />
                </figure>
              ))}
            </div>
          </section>
        )}

        <section className="cv-section" id="cv-features">
          <h2 className="cv-section-heading">{FEATURES.heading}</h2>
          <div className="cv-features">
            {FEATURES.cards.map((card) => (
              <article className="cv-card" key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="cv-section" id="cv-civilizations">
          <div className="cv-card cv-card-dialog">
            <h2>{CIVILIZATIONS.heading}</h2>
            <p className="cv-card-intro">{CIVILIZATIONS.intro}</p>
            <ul className="cv-civs">
              {CIVILIZATIONS.list.map((civ) => {
                const portrait = siteImages[`civitas-leader-${civ.key}`];
                return (
                  <li className="cv-civ" key={civ.name} style={{ backgroundColor: civ.tint }}>
                    <img
                      src={portrait.src}
                      srcSet={portrait.srcSet}
                      sizes="80px"
                      width={portrait.width}
                      height={portrait.height}
                      alt={civ.leader}
                      className="cv-civ-portrait"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="cv-civ-text">
                      <span className="cv-civ-name">{civ.name}</span>
                      <span className="cv-civ-leader">{civ.leader}</span>
                      <span className="cv-civ-buff">
                        <b>{civ.buff.name}:</b> {civ.buff.description}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      </main>

      <footer className="cv-footer">
        <p>{FOOTER.status}</p>
        <p>{FOOTER.credits}</p>
        <ul className="cv-footer-links">
          <li><a href={APP_STORE_URL}>{FOOTER.links.appStore}</a></li>
          <li><a href={X_URL} target="_blank" rel="noopener noreferrer">{FOOTER.links.x}</a></li>
          <li><Link to={PRIVACY_PATH}>{FOOTER.links.privacy}</Link></li>
        </ul>
      </footer>
    </div>
  );
};

export default CivitasPage;